export type TrackId = "network" | "ai" | "infrastructure" | "communication";

export interface Phrase {
  english: string;
  chinese: string;
  intent: string;
}

export interface LessonStage {
  id: "input" | "phrases" | "roleplay";
  label: string;
  minutes: number;
  instruction: string;
}

export interface Lesson {
  id: string;
  day: number;
  phase: string;
  track: TrackId;
  title: string;
  subtitle: string;
  concept: string;
  listeningText: string;
  listeningTranslation: string;
  phrases: Phrase[];
  logicFrame: {
    name: string;
    pattern: string;
    prompt: string;
    example: string;
  };
  roleplay: {
    persona: string;
    setup: string;
    opening: string;
  };
  stages: LessonStage[];
}

export interface ContentPack {
  id: string;
  title: string;
  version: number;
  phase: string;
  updatedAt: string;
  tracks: Array<{
    id: TrackId;
    label: string;
    description: string;
    progress: number;
  }>;
  lessons: Lesson[];
}

export const defaultPack: ContentPack = {
  id: "cloudflare-work-english-foundation",
  title: "WorkingEnglish · Foundation",
  version: 2,
  phase: "foundation",
  updatedAt: "2026-09-20",
  tracks: [
    {
      id: "network",
      label: "Cloud Network",
      description: "DNS, anycast, TLS termination, caching, WAF and routing",
      progress: 36,
    },
    {
      id: "ai",
      label: "LLM & AI",
      description: "RAG, agents, inference latency, evals and token cost",
      progress: 18,
    },
    {
      id: "infrastructure",
      label: "Infrastructure",
      description: "Compute, storage, queues, observability and delivery",
      progress: 22,
    },
    {
      id: "communication",
      label: "Work Language",
      description: "Discovery, expectation-setting, trade-offs and next steps",
      progress: 41,
    },
  ],
  lessons: [
    {
      id: "network-url-to-origin-001",
      day: 1,
      phase: "foundation",
      track: "network",
      title: "From URL to origin",
      subtitle: "Explain a request path without drowning the customer in detail.",
      concept:
        "A browser resolves DNS, negotiates a TLS handshake at the edge, sends the HTTP request, and may get a cache hit before anything ever reaches the origin.",
      listeningText:
        "The short answer is that Cloudflare terminates the request at the edge, close to the user. DNS resolves to our nearest data center via anycast, the TLS handshake happens there, and static assets are served straight from cache when we get a hit. Only dynamic or uncached requests are proxied back to your origin — and even then, they go through our security stack first, so the junk traffic never reaches you.",
      listeningTranslation:
        "简单来说，Cloudflare 在离用户最近的边缘节点终止请求。DNS 通过 anycast 解析到最近的机房，TLS 握手在那里完成，静态资源命中缓存就直接返回。只有动态或未命中缓存的请求才会回源——而且回源前会先经过安全栈过滤，垃圾流量根本到不了你的源站。",
      phrases: [
        {
          english: "The short answer is that...",
          chinese: "简单来说……（先给结论）",
          intent: "Lead with the answer",
        },
        {
          english: "That traffic never actually reaches your origin — it's absorbed at the edge.",
          chinese: "这部分流量其实不会到你的源站——它在边缘就被消化掉了。",
          intent: "Correct an assumption",
        },
        {
          english: "The trade-off is cache freshness versus cache hit ratio.",
          chinese: "这里的取舍是缓存新鲜度和缓存命中率。",
          intent: "Frame a trade-off",
        },
      ],
      logicFrame: {
        name: "Answer → mechanism → impact",
        pattern: "The short answer is... / This works by... / In practice, this means...",
        prompt: "Explain why putting Cloudflare in front of an origin improves performance.",
        example:
          "The short answer is that users are served from the closest edge location instead of your origin. This works by terminating TLS at the edge and caching static assets on our global network. In practice, this means lower round-trip latency, a higher cache hit ratio, and significantly less load on your origin servers.",
      },
      roleplay: {
        persona: "Customer infrastructure lead",
        setup: "The customer believes every request still hits their AWS load balancer directly.",
        opening: "If you're just flipping our DNS records, how does that actually take load off our ALB?",
      },
      stages: [
        { id: "input", label: "Read & retell", minutes: 5, instruction: "Read the explanation, then retell it aloud in your own words." },
        { id: "phrases", label: "Phrase workout", minutes: 3, instruction: "Say each chunk aloud three times until it feels automatic." },
        { id: "roleplay", label: "Customer answer", minutes: 4, instruction: "Answer the customer aloud using the frame hint, then save your best version." },
      ],
    },
  ],
};
